import { useContext } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { House, SquaresFour, Compass } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { LoginAuthContext } from "Context/Authentication/LoginAuth";

const NotFound = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useContext(LoginAuthContext);
  const reduce = useReducedMotion();

  const homeTo = isAuthenticated ? "/categories" : "/";
  const rise = (delay) =>
    reduce
      ? {}
      : {
          initial: { y: 24, opacity: 0 },
          animate: { y: 0, opacity: 1 },
          transition: { delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
        };

  return (
    <section
      className="relative isolate overflow-hidden bg-bg text-white min-h-[80vh] flex items-center justify-center px-6 py-20"
      role="region"
      aria-labelledby="nf-title"
    >
      {/* Ambient brand glow — decorative */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/3 h-[38rem] w-[38rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/20 blur-[130px]" />
        <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-darkPrimary/30 blur-[110px]" />
      </div>

      <div className="relative z-10 mx-auto flex max-w-2xl flex-col items-center text-center">
        {/* 404 */}
        <motion.div {...rise(0)} className="relative">
          <span
            aria-hidden="true"
            className="block bg-gradient-to-b from-white via-primary to-darkPrimary bg-clip-text text-[7rem] font-extrabold leading-none tracking-tight text-transparent sm:text-[10rem]"
          >
            {t("not_found.code")}
          </span>
          <span className="mx-auto mt-5 block h-1 w-16 rounded-full bg-primary" />
        </motion.div>

        {/* Copy */}
        <motion.h1
          id="nf-title"
          {...rise(0.1)}
          className="mt-8 text-2xl font-semibold text-white sm:text-3xl"
        >
          {t("not_found.title")}
        </motion.h1>
        <motion.p
          {...rise(0.18)}
          className="mt-4 max-w-md text-base leading-relaxed text-lightWhite"
        >
          {t("not_found.subtitle")}
        </motion.p>

        {/* Actions */}
        <motion.div
          {...rise(0.26)}
          className="mt-10 flex w-full flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <Link
            to={homeTo}
            state={{ isAuthenticated }}
            className="inline-flex min-h-[48px] w-full items-center justify-center gap-x-2 rounded-xl bg-primary px-7 font-medium text-white shadow-lg shadow-primary/25 transition-all duration-200 hover:bg-primary/90 hover:shadow-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg active:scale-[0.98] sm:w-auto"
          >
            <House weight="fill" size={20} />
            {t("not_found.home")}
          </Link>
          <Link
            to="/categories"
            state={{ isAuthenticated }}
            className="inline-flex min-h-[48px] w-full items-center justify-center gap-x-2 rounded-xl border border-white/15 bg-white/5 px-7 font-medium text-white transition-all duration-200 hover:border-white/30 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-bg active:scale-[0.98] sm:w-auto"
          >
            {isAuthenticated ? (
              <SquaresFour weight="fill" size={20} />
            ) : (
              <Compass weight="fill" size={20} />
            )}
            {t("not_found.browse")}
          </Link>
        </motion.div>

        {/* Support */}
        <motion.p {...rise(0.34)} className="mt-10 text-sm text-lightWhite">
          {t("not_found.help")}{" "}
          <Link
            to="/contact"
            className="font-medium text-primary underline-offset-4 transition-colors hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            {t("not_found.contact")}
          </Link>
        </motion.p>
      </div>
    </section>
  );
};

export default NotFound;
